import { ApiError, type CarrierResolveResult } from '@commissionsight/sdk';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { EmailIngestSettings } from '../components/EmailIngestSettings.js';
import { FILES_TABS, PageTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { SftpSettings } from '../components/SftpSettings.js';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  ICONS,
  PausedBanner,
  Spinner,
  inputClass,
} from '../components/ui.js';
import { useCarrierGroups, useCarriers } from '../lib/useCarriers.js';

const PRODUCT_LINE_LABEL: Record<string, string> = {
  major_medical: 'Major medical',
  medicare: 'Medicare',
  ancillary: 'Ancillary',
};
const plLabel = (pl: string) => PRODUCT_LINE_LABEL[pl] ?? pl;

interface FileRow {
  id: string;
  carrierId: string;
  periodYear: number;
  periodMonth: number;
  originalFilename: string;
  workspaceName?: string | null;
  rescoreSuggested?: boolean;
  r2PurgedAt?: number | string | null;
}
interface UploadItem {
  name: string;
  state: 'pending' | 'uploading' | 'queued' | 'error' | 'conflict' | 'duplicate';
  jobId?: string;
  error?: string;
  // Retained so a `conflict` (period already exists) can be re-sent with
  // `replace: true` in one click, using the ORIGINAL upload's params.
  file?: File;
  carrierId?: string;
  periodYear?: number;
  periodMonth?: number;
  workspaceId?: string;
}

/** A pending confirmation for a destructive action (replace or retract). */
interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  tone: 'primary' | 'danger';
  onConfirm: () => Promise<void>;
}

function isPeriodExists(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 409 &&
    (e.body as { code?: string } | undefined)?.code === 'period_exists'
  );
}

/**
 * Upload a single statement carrying an optional carrier-stated total. `statedTotal`
 * is deliberately absent from the public SDK, so we post the same multipart form the
 * SDK builds directly and re-throw an `ApiError` on failure so the existing
 * conflict/idempotency handling (e.g. `isPeriodExists`) works unchanged.
 */
async function uploadWithStatedTotal(opts: {
  baseUrl: string;
  token: string | null;
  file: File;
  carrierId: string;
  periodYear: number;
  periodMonth: number;
  workspaceId?: string;
  statedTotal: string;
}): Promise<{ jobId: string; status: string; idempotent?: boolean }> {
  const form = new FormData();
  form.set('file', opts.file);
  form.set('carrierId', opts.carrierId);
  form.set('periodYear', String(opts.periodYear));
  form.set('periodMonth', String(opts.periodMonth));
  if (opts.workspaceId) form.set('workspaceId', opts.workspaceId);
  form.set('statedTotal', opts.statedTotal);
  const res = await fetch(`${opts.baseUrl.replace(/\/$/, '')}/files`, {
    method: 'POST',
    headers: opts.token ? { authorization: `Bearer ${opts.token}` } : undefined,
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (body as { title?: string }).title ?? 'Upload failed', body);
  }
  return body as { jobId: string; status: string; idempotent?: boolean };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const periodLabel = (y: number, m: number) => `${MONTHS[m - 1] ?? m} ${y}`;

export function Files() {
  const { client, token, baseUrl, processingPaused, workspacesEnabled, workspaces } = useAuth();
  const { carriers } = useCarriers();
  const { groups } = useCarrierGroups();
  const [files, setFiles] = useState<FileRow[]>([]);
  // carrierId → workspaceIds it already has data in (upload split-guard).
  const [carrierWs, setCarrierWs] = useState<Record<string, string[]>>({});
  // Carrier picker value: "carrier:<id>" (upload directly) or "group:<id>"
  // (a brand → detect the concrete product from the chosen file).
  const [selection, setSelection] = useState('');
  // Result of detecting which product a brand's file is.
  const [classify, setClassify] = useState<{
    status: 'idle' | 'loading' | 'done' | 'error';
    result?: CarrierResolveResult;
    error?: string;
  }>({ status: 'idle' });
  // The concrete carrier chosen for a brand selection (best detection or override).
  const [detected, setDetected] = useState('');
  const [period, setPeriod] = useState('2025-01');
  const [workspaceId, setWorkspaceId] = useState('');
  // Optional carrier-stated statement total, reconciled against the ingested sum.
  // Applies only when uploading a single statement (the total is per-statement).
  const [statedTotal, setStatedTotal] = useState('');
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [busy, setBusy] = useState(false);
  // File ids currently being re-scored — drives a real, data-polled spinner.
  const [rescoring, setRescoring] = useState<Set<string>>(new Set());
  // File ids currently being retracted (DELETE in flight).
  const [retracting, setRetracting] = useState<Set<string>>(new Set());
  // File ids currently being purged from R2.
  const [purging, setPurging] = useState<Set<string>>(new Set());
  // Pending destructive-action confirmation (replace / retract), or null.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Picker derivations. Grouped carriers are reachable via their brand's
  // auto-detect, so only standalone carriers are offered individually.
  const ungrouped = carriers.filter((c) => !c.groupId);
  const selKind: 'group' | 'carrier' = selection.startsWith('group:') ? 'group' : 'carrier';
  const selGroupId = selKind === 'group' ? selection.slice('group:'.length) : '';
  const selCarrierId = selKind === 'carrier' ? selection.slice('carrier:'.length) : '';
  // The concrete carrier the upload will target (a brand resolves via detection).
  const uploadCarrierId = selKind === 'group' ? detected : selCarrierId;
  const selectedGroup = groups.find((g) => g.id === selGroupId);

  // Split guard: uploading a carrier whose history lives in ANOTHER workspace (and
  // none yet in the chosen one) would fork its month-over-month tracking. Warn, don't block.
  const carrierWorkspaces = uploadCarrierId ? (carrierWs[uploadCarrierId] ?? []) : [];
  const wouldSplit =
    workspacesEnabled &&
    !!uploadCarrierId &&
    !!workspaceId &&
    carrierWorkspaces.length > 0 &&
    !carrierWorkspaces.includes(workspaceId);
  const splitWorkspaceNames = wouldSplit
    ? carrierWorkspaces.map(
        (id) => workspaces.find((w) => w.id === id)?.name ?? 'another workspace',
      )
    : [];

  const carrierName = (id: string) => carriers.find((c) => c.id === id)?.name ?? id.slice(0, 8);

  const refresh = () => client.listFiles({ limit: 50 }).then((r) => setFiles(r.data as FileRow[]));

  // biome-ignore lint/correctness/useExhaustiveDependencies: load files once on mount
  useEffect(() => {
    refresh();
  }, [client]);

  // Default the picker: a standalone carrier if any, else the first brand.
  useEffect(() => {
    if (selection) return;
    if (ungrouped[0]) setSelection(`carrier:${ungrouped[0].id}`);
    else if (groups[0]) setSelection(`group:${groups[0].id}`);
  }, [ungrouped, groups, selection]);

  // Detect the concrete product for a brand from the chosen file.
  const runClassify = async (groupId: string, file: File) => {
    setClassify({ status: 'loading' });
    setDetected('');
    try {
      const result = await client.resolveCarrier(groupId, file);
      setClassify({ status: 'done', result });
      if (result.best && !result.ambiguous) setDetected(result.best.carrierId);
    } catch (e) {
      setClassify({
        status: 'error',
        error: e instanceof Error ? e.message : 'Could not detect the product',
      });
    }
  };

  // Re-evaluate detection when the chosen file or the selected brand changes.
  const reclassify = () => {
    if (selKind !== 'group') {
      setClassify({ status: 'idle' });
      setDetected('');
      return;
    }
    const sel = Array.from(fileRef.current?.files ?? []);
    if (sel.length === 0) {
      setClassify({ status: 'idle' });
      setDetected('');
      return;
    }
    if (sel.length > 1) {
      setClassify({
        status: 'error',
        error:
          'Auto-detect handles one file at a time — select a single file, or pick a specific carrier.',
      });
      setDetected('');
      return;
    }
    const file = sel[0];
    if (file) void runClassify(selGroupId, file);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-detect on brand switch
  useEffect(() => {
    reclassify();
  }, [selection]);

  // Default the workspace picker to the account's default workspace once loaded.
  useEffect(() => {
    if (workspaceId || workspaces.length === 0) return;
    setWorkspaceId((workspaces.find((w) => w.isDefault) ?? workspaces[0])?.id ?? '');
  }, [workspaces, workspaceId]);

  // Which workspaces each carrier already has data in — powers the split guard.
  useEffect(() => {
    if (!workspacesEnabled) return;
    let active = true;
    fetch(`${baseUrl.replace(/\/$/, '')}/carriers/workspace-usage`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!active || !b) return;
        const map: Record<string, string[]> = {};
        for (const u of b.usage as { carrierId: string; workspaceIds: string[] }[])
          map[u.carrierId] = u.workspaceIds;
        setCarrierWs(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, workspacesEnabled]);

  const upload = async () => {
    const selected = Array.from(fileRef.current?.files ?? []);
    if (selected.length === 0 || !uploadCarrierId) return;
    if (workspacesEnabled && !workspaceId) return;
    const target = uploadCarrierId;
    const wsId = workspacesEnabled ? workspaceId : undefined;
    const [y, m] = period.split('-');
    const periodYear = Number(y);
    const periodMonth = Number(m);
    setBusy(true);
    setUploads(
      selected.map((f) => ({
        name: f.name,
        state: 'pending' as const,
        file: f,
        carrierId: target,
        periodYear,
        periodMonth,
        workspaceId: wsId,
      })),
    );

    // A stated total is per-statement, so only apply it to a single-file upload.
    const totalToSend =
      selected.length === 1 && statedTotal.trim() !== '' ? statedTotal.trim() : null;

    // Fan out one queued ingest job per file (the queue processes them independently).
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      if (!file) continue;
      setUploads((u) => u.map((x, idx) => (idx === i ? { ...x, state: 'uploading' } : x)));
      try {
        // The server decides by content hash: a byte-identical re-upload comes
        // back `idempotent` (no-op); different content for an existing period
        // throws 409 `period_exists` → handled below as a Replace prompt.
        // statedTotal is deliberately NOT on the public SDK, so when supplied we
        // post the multipart form directly (same fields the SDK would send).
        const res = totalToSend
          ? await uploadWithStatedTotal({
              baseUrl,
              token,
              file,
              carrierId: target,
              periodYear,
              periodMonth,
              workspaceId: wsId,
              statedTotal: totalToSend,
            })
          : await client.uploadFile({
              file,
              carrierId: target,
              periodYear,
              periodMonth,
              workspaceId: wsId,
            });
        const dup = (res as { idempotent?: boolean }).idempotent === true;
        setUploads((u) =>
          u.map((x, idx) =>
            idx === i ? { ...x, state: dup ? 'duplicate' : 'queued', jobId: res.jobId } : x,
          ),
        );
      } catch (e) {
        // A period that already has a file isn't an error — it's a replace
        // decision. Surface it as a conflict the user can confirm.
        const state = isPeriodExists(e) ? 'conflict' : 'error';
        setUploads((u) =>
          u.map((x, idx) =>
            idx === i ? { ...x, state, error: e instanceof Error ? e.message : 'failed' } : x,
          ),
        );
      }
    }
    if (fileRef.current) fileRef.current.value = '';
    setStatedTotal('');
    setClassify({ status: 'idle' });
    setDetected('');
    setBusy(false);
    refresh();
  };

  // Re-send a conflicted upload with `replace: true` — retracts the existing
  // period and re-ingests this file (see the corrected-file workflow).
  const replaceUpload = async (i: number) => {
    const item = uploads[i];
    if (!item?.file || !item.carrierId) return;
    setUploads((u) => u.map((x, idx) => (idx === i ? { ...x, state: 'uploading' } : x)));
    try {
      // replace=true bypasses the gate and retract-then-reingests; the confirm
      // dialog's busy state already guards against a double submit.
      const res = await client.uploadFile({
        file: item.file,
        carrierId: item.carrierId,
        periodYear: item.periodYear ?? 0,
        periodMonth: item.periodMonth ?? 0,
        replace: true,
        workspaceId: item.workspaceId,
      });
      setUploads((u) =>
        u.map((x, idx) => (idx === i ? { ...x, state: 'queued', jobId: res.jobId } : x)),
      );
      refresh();
    } catch (e) {
      setUploads((u) =>
        u.map((x, idx) =>
          idx === i
            ? { ...x, state: 'error', error: e instanceof Error ? e.message : 'failed' }
            : x,
        ),
      );
    }
  };

  // Retract (unapply) a file's period entirely. The API marks it superseded
  // synchronously, so it drops off the list on the next refresh.
  const retract = async (fileId: string) => {
    setRetracting((s) => new Set(s).add(fileId));
    try {
      await client.retractFile(fileId);
      await refresh();
    } finally {
      setRetracting((s) => {
        const next = new Set(s);
        next.delete(fileId);
        return next;
      });
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.onConfirm();
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  // Trigger an async re-score, then poll the files list until the engine clears
  // this period's stale flag (a real completion signal — not a fixed timer).
  const rescore = async (fileId: string) => {
    setRescoring((s) => new Set(s).add(fileId));
    try {
      await client.rescoreFile(fileId);
    } catch {
      setRescoring((s) => {
        const next = new Set(s);
        next.delete(fileId);
        return next;
      });
      return;
    }
    for (let attempt = 0; attempt < 12; attempt++) {
      await new Promise((r) => setTimeout(r, 1500));
      const r = await client.listFiles({ limit: 50 });
      const data = r.data as FileRow[];
      setFiles(data);
      const row = data.find((f) => f.id === fileId);
      if (!row?.rescoreSuggested) break; // re-score landed — flag cleared
    }
    setRescoring((s) => {
      const next = new Set(s);
      next.delete(fileId);
      return next;
    });
  };

  // Purge the raw bytes from object storage (data retention); keeps the row.
  const purge = async (fileId: string) => {
    setPurging((s) => new Set(s).add(fileId));
    try {
      await client.purgeFile(fileId);
      await refresh();
    } finally {
      setPurging((s) => {
        const next = new Set(s);
        next.delete(fileId);
        return next;
      });
    }
  };

  const staleCount = files.filter((f) => f.rescoreSuggested).length;

  return (
    <div className="space-y-6">
      <PageTabs tabs={FILES_TABS} />
      {processingPaused && (
        <PausedBanner message="Statement processing is paused for your account. Add a payment method on the Profile page or contact support to resume — new uploads won't be processed until then." />
      )}
      <Card>
        <h2 className="mb-1 font-semibold">Upload a statement</h2>
        <p className="mb-4 text-sm text-neutral-500">
          New carrier? You don’t have to map columns by hand —{' '}
          <strong>just upload the statement</strong> and CommissionSight auto-detects the format and
          builds the mapping for you (it learns each statement layout once, then reuses it on every
          future upload). Prefer to do it yourself, or want to review the mapping first?{' '}
          <Link to="/carrier-setup" className="font-medium text-emerald-600 hover:underline">
            Set up a carrier
          </Link>
          .
        </p>
        <div className="grid gap-4 md:grid-cols-4">
          {workspacesEnabled && (
            <Field label="Workspace">
              <select
                className={inputClass}
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
              >
                <option value="">Select a workspace…</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Carrier">
            <select
              className={inputClass}
              value={selection}
              onChange={(e) => setSelection(e.target.value)}
            >
              {groups.map((g) => (
                <option key={`group:${g.id}`} value={`group:${g.id}`}>
                  {g.name} (auto-detect)
                </option>
              ))}
              {ungrouped.map((c) => (
                <option key={`carrier:${c.id}`} value={`carrier:${c.id}`}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Period">
            <PeriodPicker value={period} onChange={setPeriod} />
          </Field>
          <Field
            label={
              selKind === 'group'
                ? 'File (CSV/XLSX — one at a time)'
                : 'Files (CSV/XLSX — select multiple)'
            }
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              multiple={selKind !== 'group'}
              onChange={reclassify}
              className={inputClass}
            />
          </Field>
          <Field label="Statement total (optional)">
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={statedTotal}
              onChange={(e) => setStatedTotal(e.target.value)}
              placeholder="e.g. 12345.67"
              className={inputClass}
            />
          </Field>
          <div className="flex items-end">
            <Button
              onClick={upload}
              loading={busy}
              disabled={processingPaused || (workspacesEnabled && !workspaceId) || !uploadCarrierId}
            >
              {busy ? 'Uploading…' : 'Upload & queue'}
            </Button>
          </div>
        </div>
        {wouldSplit && (
          <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            ⚠ <strong>{carrierName(uploadCarrierId)}</strong> already has data in{' '}
            <strong>{splitWorkspaceNames.join(', ')}</strong>. Uploading it here splits that
            carrier's month-over-month history across workspaces — upload to the same workspace to
            keep it together, unless you intend a separate book here.
          </p>
        )}
        {selKind === 'group' && (
          <DetectionPanel
            brandName={selectedGroup?.name ?? 'this brand'}
            classify={classify}
            detected={detected}
            onChange={setDetected}
          />
        )}
        <p className="mt-2 text-xs text-neutral-500">
          All selected files queue for the chosen carrier and period, and process independently. The
          optional statement total is reconciled against the commission we ingest (to catch dropped
          rows) and applies only when you upload a single statement.
        </p>
        {uploads.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {uploads.map((u, i) => (
              <li
                key={`${u.name}-${i}`}
                className="flex items-center justify-between rounded-md bg-neutral-50 px-3 py-2 text-sm dark:bg-neutral-800/50"
              >
                <span className="truncate pr-3">{u.name}</span>
                <div className="flex shrink-0 items-center gap-2">
                  {u.state === 'conflict' && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setConfirm({
                          title: 'Replace existing statement?',
                          message: `A statement for ${carrierName(u.carrierId ?? '')} · ${periodLabel(
                            u.periodYear ?? 0,
                            u.periodMonth ?? 0,
                          )} already exists. Replacing deletes its data and recomputes the period — including re-scoring the following month. This can't be undone.`,
                          confirmLabel: 'Replace',
                          tone: 'primary',
                          onConfirm: () => replaceUpload(i),
                        })
                      }
                    >
                      Replace…
                    </Button>
                  )}
                  <UploadBadge item={u} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-1 font-semibold">Recent uploads</h2>
        {staleCount > 0 && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
            {staleCount === 1 ? '1 period was' : `${staleCount} periods were`} scored before an
            earlier month was uploaded. Re-process to recompute their statuses against the new
            baseline — this only touches the flagged month, not later periods.
          </div>
        )}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {files.length === 0 && (
            <EmptyState
              icon={ICONS.inbox}
              title="No files yet"
              hint="Upload a carrier statement above to score your first period."
            />
          )}
          {files.map((f) => {
            const isRescoring = rescoring.has(f.id);
            const isRetracting = retracting.has(f.id);
            return (
              <div key={f.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <div className="truncate">{f.originalFilename}</div>
                  <div className="text-xs text-neutral-400">
                    {carrierName(f.carrierId)} · {periodLabel(f.periodYear, f.periodMonth)}
                    {workspacesEnabled && f.workspaceName ? (
                      <span className="ml-1.5 rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                        {f.workspaceName}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {f.rescoreSuggested && (
                    <>
                      <span
                        className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        title="An earlier baseline month was uploaded after this period was scored."
                      >
                        baseline updated
                      </span>
                      <Button variant="ghost" onClick={() => rescore(f.id)} loading={isRescoring}>
                        {isRescoring ? 'Re-processing…' : 'Re-process'}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                    loading={isRetracting}
                    onClick={() =>
                      setConfirm({
                        title: 'Remove this statement?',
                        message: `This deletes the ${carrierName(f.carrierId)} · ${periodLabel(
                          f.periodYear,
                          f.periodMonth,
                        )} data and re-scores the following month. This can't be undone.${
                          files.length === 1
                            ? ' This is your only loaded statement — removing it leaves your account with no data. To move it to another workspace, re-upload it there instead of removing it here.'
                            : ''
                        }`,
                        confirmLabel: 'Remove',
                        tone: 'danger',
                        onConfirm: () => retract(f.id),
                      })
                    }
                  >
                    {isRetracting ? 'Removing…' : 'Remove'}
                  </Button>
                  {f.r2PurgedAt ? (
                    <span
                      className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-500 dark:bg-neutral-700 dark:text-neutral-300"
                      title="The raw statement file has been purged from storage."
                    >
                      raw purged
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={purging.has(f.id)}
                      onClick={() =>
                        setConfirm({
                          title: 'Purge raw file?',
                          message: `This permanently deletes the raw ${carrierName(f.carrierId)} · ${periodLabel(
                            f.periodYear,
                            f.periodMonth,
                          )} statement from storage. Scored results stay, but the file can no longer be re-processed.`,
                          confirmLabel: 'Purge',
                          tone: 'danger',
                          onConfirm: () => purge(f.id),
                        })
                      }
                    >
                      {purging.has(f.id) ? 'Purging…' : 'Purge raw'}
                    </Button>
                  )}
                  <span className="font-mono text-xs text-neutral-400">{f.id.slice(0, 8)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <SftpSettings />
      <EmailIngestSettings />

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        loading={confirmBusy}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function UploadBadge({ item }: { item: UploadItem }) {
  if (item.state === 'queued')
    return (
      <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
        queued · {item.jobId?.slice(0, 8)}
      </span>
    );
  if (item.state === 'duplicate')
    return (
      <span
        className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
        title="This exact file was already uploaded for this period — no change made."
      >
        already loaded
      </span>
    );
  if (item.state === 'conflict')
    return (
      <span
        className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        title="A statement for this carrier and period already exists."
      >
        period exists
      </span>
    );
  if (item.state === 'error')
    return (
      <span
        className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700"
        title={item.error}
      >
        failed
      </span>
    );
  if (item.state === 'uploading')
    return <span className="shrink-0 text-xs text-neutral-500">uploading…</span>;
  return <span className="shrink-0 text-xs text-neutral-400">waiting</span>;
}

/**
 * Shows the result of detecting which product a brand's file is. The user always
 * confirms via the override dropdown before upload (forced when ambiguous) — the
 * resolved carrier is what gets uploaded, so a mis-route can't silently happen.
 */
function DetectionPanel({
  brandName,
  classify,
  detected,
  onChange,
}: {
  brandName: string;
  classify: {
    status: 'idle' | 'loading' | 'done' | 'error';
    result?: CarrierResolveResult;
    error?: string;
  };
  detected: string;
  onChange: (v: string) => void;
}) {
  if (classify.status === 'idle')
    return (
      <p className="mt-3 text-xs text-neutral-500">
        Choose a single file and we'll detect which {brandName} product it is.
      </p>
    );
  if (classify.status === 'loading')
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-neutral-500">
        <Spinner className="h-4 w-4" /> Detecting product…
      </div>
    );
  if (classify.status === 'error')
    return <p className="mt-3 text-sm text-rose-600">{classify.error}</p>;
  const result = classify.result;
  if (!result) return null;
  return (
    <div
      className={`mt-3 rounded-lg border px-3 py-2.5 text-sm ${
        result.ambiguous
          ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40'
          : 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30'
      }`}
    >
      {result.ambiguous ? (
        <p className="text-amber-800 dark:text-amber-300">
          Couldn't confidently tell which {brandName} product this is — please confirm:
        </p>
      ) : (
        <p className="text-emerald-800 dark:text-emerald-300">
          Detected <b>{result.best?.name}</b>
          {result.best ? ` — ${plLabel(result.best.productLine)}` : ''} (
          {Math.round((result.best?.confidence ?? 0) * 100)}% match). Override below if needed.
        </p>
      )}
      <select
        className={`${inputClass} mt-2`}
        value={detected}
        onChange={(e) => onChange(e.target.value)}
      >
        {result.ambiguous && <option value="">Select the product…</option>}
        {result.ranked.map((r) => (
          <option key={r.carrierId} value={r.carrierId}>
            {r.name} — {plLabel(r.productLine)} ({Math.round(r.confidence * 100)}%)
          </option>
        ))}
      </select>
    </div>
  );
}
